/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';

import {
  PickingListBySKUs,
  PickingListBySKUsUpdatePayload,
  PickingRecommendation,
  PickingTask,
  PickingTaskState,
  PickingTaskValidation,
  ProductDetail,
} from 'commons/types';
import { ReducerKeys } from 'redux-stores/enums/reducerKey.enum';
import { IPickingJobVerifyBarcodeRequest } from 'services/apis/SwipeRxWmsApiPickJob/types';

const initialState: PickingTaskState = {
  tasks: [],
  assignedTasks: [],
  tasksBySKUs: [],
  skuList: [],
  skuLowStockTriggered: {},
  invalidSKUs: {},
  binRecommendationInfo: [
    {
      bin: '-',
      bin_type: '-',
      inventory_number: '-',
      quantity: 0,
    } as ProductDetail,
  ],
};

const pickingTaskSlice = createSlice({
  name: ReducerKeys.PickingTask,
  initialState,
  reducers: {
    setAssignedTask(
      state: PickingTaskState,
      action: { type: string; payload: PickingTask[] },
    ) {
      state.assignedTasks = action.payload;
    },
    addAssignedTask(
      state: PickingTaskState,
      action: { type: string; payload: PickingTask },
    ) {
      state.assignedTasks.push(action.payload);
    },
    removeAssignedTask(
      state: PickingTaskState,
      action: { type: string; payload: PickingTask },
    ) {
      const updatedTasks = state.assignedTasks.filter(
        (task) => task.po_number !== action.payload.po_number,
      );
      state.assignedTasks = updatedTasks;
    },
    updateAssignedTask(
      state: PickingTaskState,
      action: { type: string; payload: PickingTask },
    ) {
      const updatedTasks = state.assignedTasks.map((task) =>
        task.po_number === action.payload.po_number ? action.payload : task,
      );
      state.assignedTasks = updatedTasks;
    },
    updateTaskBySKU(
      state: PickingTaskState,
      action: { type: string; payload: PickingListBySKUs[] },
    ) {
      state.tasksBySKUs = action.payload;
    },
    updateSKUList(
      state: PickingTaskState,
      action: { type: string; payload: string[] },
    ) {
      state.skuList = action.payload;
    },
    updateTaskBySKUPickedActual(
      state: PickingTaskState,
      action: { type: string; payload: PickingListBySKUsUpdatePayload },
    ) {
      const { payload } = action;
      const updatedTaskBySkus = state.tasksBySKUs.map((task) => ({
        ...task,
        details: task.details.map((detail) => ({
          ...detail,
          picked_actual: payload[detail.po_number]
            ? [...payload[detail.po_number]]
            : detail.picked_actual,
        })),
      }));
      state.tasksBySKUs = updatedTaskBySkus;
    },
    updateTaskBySKUBarcodeScanned(
      state: PickingTaskState,
      action: {
        type: string;
        payload: {
          params: IPickingJobVerifyBarcodeRequest;
          barcodeScanned: boolean;
        };
      },
    ) {
      const { params, barcodeScanned } = action.payload;
      const updatedTaskBySkus = state.tasksBySKUs.map((task) => ({
        ...task,
        is_barcode_scanned:
          task.sku_code === params.sku_code
            ? barcodeScanned
            : task.is_barcode_scanned,
      }));
      state.tasksBySKUs = updatedTaskBySkus;
    },
    updateTaskBySKUPickRecommendation(
      state: PickingTaskState,
      action: {
        type: string;
        payload: {
          sku_code: string;
          pick_infos?: PickingRecommendation[];
        };
      },
    ) {
      const { sku_code: skuCode, pick_infos: pickInfos } = action.payload;
      const pickRecommendation = pickInfos || [];
      const updatedTaskBySkus = state.tasksBySKUs.map((task) => {
        if (task.sku_code === skuCode) {
          return {
            ...task,
            pick_recommendation: pickRecommendation,
          };
        }
        return task;
      });
      state.tasksBySKUs = updatedTaskBySkus;
    },
    /**
     * To give temporary flag to sku that is stock out
     * whether picker has triggered the replenish button request or not
     * @param state
     * @param action
     */
    updateTaskBySKUReplenishRequested(
      state: PickingTaskState,
      action: {
        type: string;
        payload: {
          sku_code: string;
          replenish_requested: boolean;
        };
      },
    ) {
      const { sku_code: skuCode, replenish_requested: replenishRequested } =
        action.payload;
      const updatedData = state.skuLowStockTriggered;
      updatedData[skuCode] = replenishRequested;
      state.skuLowStockTriggered = updatedData;
    },
    updateInvalidSKUs(
      state: PickingTaskState,
      action: {
        type: string;
        payload: {
          [sku_code: string]: PickingTaskValidation;
        };
      },
    ) {
      state.invalidSKUs = action.payload;
    },

    setPickItemBinRecommendationInfo(
      state: PickingTaskState,
      action: { type: string; payload: ProductDetail[] },
    ) {
      state.binRecommendationInfo = action.payload;
    },
  },
});

export const {
  setAssignedTask,
  addAssignedTask,
  updateAssignedTask,
  removeAssignedTask,
  updateTaskBySKU,
  updateTaskBySKUPickRecommendation,
  updateTaskBySKUPickedActual,
  updateTaskBySKUBarcodeScanned,
  updateSKUList,
  updateTaskBySKUReplenishRequested,
  updateInvalidSKUs,
  setPickItemBinRecommendationInfo,
} = pickingTaskSlice.actions;

export default pickingTaskSlice.reducer;
